import React, { useState, useEffect } from 'react';
import NodeCredentialsPanel from './NodeCredentialsPanel';
import { InstallerBuildPanel } from './InstallerBuildPanel';
import { useNetworkHelpers } from './useNetworkHelpers';
import SectionedDialog from './SectionedDialog';

// Helper inventory used to live as a hardcoded KNOWN_HELPERS array
// here — every network's Add Node showed the same 16 entries, and
// adding/removing a helper required a code change + redeploy.
//
// As of the root-node-type slug, the network's root node owns the
// inventory: NodeService.get_helpers (in aveq-aggregator/aggregators/
// root/node_service.py) reads from a `helpers/` directory and aveq-
// admin's BFF (`/api/networks/{id}/helpers`) proxies the call. The
// picker below renders whatever that returns.
//
// Custom helpers dropped via EXTRA_ENDPOINT_DIR on the recipient
// (operator-mounted /opt/ext/extra/*.py) aren't part of root's
// inventory and aren't subject to helpers_included filtering — they
// always load on the recipient regardless of what the picker shows.

// Helper-specific build-time config declarations.
//
// Each entry describes an install-time value that one or more helpers
// consume at runtime. When at least one `consumedBy` helper is in the
// recipient's selected set, the wizard inserts a config step between
// the main form and submission. On submit, the wizard expands one
// `helper_configs[<helper>] = payloadFor(value)` entry per consuming
// helper that's selected. The backend looks up the matching processor
// (aveq-admin/helper_processors.py) and applies it — env stamp,
// node_prop write, bundle file, whatever the helper needs.
//
// Adding a new helper-with-config:
//   1. Add an entry here (UI side).
//   2. Add a HelperProcessor subclass + PROCESSORS registry entry
//      in aveq-admin/helper_processors.py (backend side).
//
// Schema:
//   id           — wizard step + envValues key (string, unique)
//   title        — header on the wizard step
//   description  — short context shown above the field
//   consumedBy   — helper module names this config feeds
//   payloadFor   — (value) => dict, the per-helper config sent to the
//                  backend processor. Shape is contract between this
//                  declaration and the matching HelperProcessor.
//   placeholder  — input placeholder
//   validate     — (value) => null | errorString
// Operators often paste config snippets from docs / Slack / Notion which
// auto-convert ASCII " to U+201C/U+201D smart quotes. JSON.parse rejects
// those. Normalize before parse + before storing so the recipient .env
// always has straight quotes regardless of where the operator typed.
function _normalizeJSONish(s) {
  return s
    .replace(/[“”]/g, '"')   // “ ” → "
    .replace(/[‘’]/g, "'")   // ‘ ’ → '
    .replace(/ /g, ' ');           // non-breaking space → space
}

const HELPER_ENV_CONFIGS = [
  {
    id: 'sensor_picos',
    title: 'Configure sensor — Picos',
    description: 'JSON array of Pico endpoints the sensor helpers poll. Used by sensor_endpoints / pistation_sensor_endpoints at runtime.',
    consumedBy: ['sensor_endpoints', 'pistation_sensor_endpoints'],
    payloadFor: (value) => ({ picos: _normalizeJSONish(value) }),
    placeholder: '[{"name":"pico2","host":"pico2.local","port":8080}]',
    validate: (raw) => {
      const s = _normalizeJSONish((raw || '').trim());
      if (!s) return null; // empty is allowed — recipient can fill later
      try {
        const v = JSON.parse(s);
        if (!Array.isArray(v)) return 'must be a JSON array';
        for (const e of v) {
          if (!e || typeof e !== 'object') return 'entry is not an object';
          if (!e.name) return 'entry missing name';
          if (!e.host && !e.hostname) return `entry ${e.name} missing host`;
        }
        return null;
      } catch (ex) {
        return String(ex.message || ex);
      }
    },
  },
];

/** Add Node form. Modal flow:
 *   1. Form view — name + parent dropdown + substrate radio + (for
 *      standalone substrates) helpers field.
 *   2. On submit:
 *      - in-stack substrate: POST /api/nodes/, show loading state, then
 *        render the credentials reveal view.
 *      - standalone-* substrate: POST /api/nodes/standalone, get back
 *        {node, installer_task_id}; show credentials + a "building
 *        installer..." spinner; poll /api/installer/{task_id} for the
 *        .run; show a "Download" button once ready.
 *
 * Substrate values + how each maps to the BFF:
 *   in-stack            → POST /api/nodes/                {name, parent_node_id?, internal_host}
 *   standalone-docker   → POST /api/nodes/standalone       {..., flavor: "docker", helpers_*?}
 *   standalone-bare-metal → POST /api/nodes/standalone     {..., flavor: "bare-metal", helpers_*?}
 *   pico                → POST /api/nodes/pico             {..., wifi_ssid?, wifi_psk?} → application/zip download
 *
 * The `pico` substrate (lite-node slug) takes a separate path because
 * the artifact is a 4-file zip (not an installer .run); the BFF
 * stamps config.json from the minted creds + the operator's Wi-Fi
 * fields and streams the zip back inline.
 *
 * After v0.41 (credentials-view) creds can also be re-fetched from
 * the node detail panel, so the "save it now" wording is a soft
 * reminder rather than a hard "no second chance".
 */
/** Multi-network slug Phase 4: parent is fixed by the caller (NodeDetail's
 * Add Child Node menu). Drops the previous Parent <select> + topology
 * prop + flattenForParentList helper + the orphan option.
 *   parentNodeId    : required (number or numeric string)
 *   parentNodeName  : optional, shown in the modal title; falls back to
 *                     "node {parentNodeId}" if absent.
 *   networkId       : required for the standalone substrate (drives the
 *                     helper inventory fetch). The parent always belongs
 *                     to a network, so the caller knows it. */
export default function AddNodeModal({ parentNodeId, parentNodeName, networkId, onClose, onSuccess }) {
  const [name, setName] = useState('');
  const [substrate, setSubstrate] = useState('in-stack');
  // dev-bundle-autobuild slug (Phase 2): docker recipients are per-arch, but the
  // Add-Node wizard had no arch control — only BuildInstallerModal did — so a
  // docker add always defaulted amd64 server-side and couldn't target arm64.
  // Give it the same UA-driven default (Apple Silicon Mac → arm64, else amd64);
  // operator overrides via the radio. Only sent for the standalone-docker path.
  const [bundleArch, setBundleArch] = useState('amd64');
  useEffect(() => {
    let cancelled = false;
    const uad = navigator.userAgentData;
    if (uad && typeof uad.getHighEntropyValues === 'function') {
      uad.getHighEntropyValues(['architecture']).then((hints) => {
        if (!cancelled && hints && hints.architecture === 'arm') setBundleArch('arm64');
      }).catch(() => { /* best-effort; stays amd64 (Safari can't report the chip) */ });
    }
    return () => { cancelled = true; };
  }, []);
  // lite-node slug: when substrate === 'pico', operator may supply Wi-Fi
  // creds inline (stamped into the bundled config.json). Both optional —
  // the recipient can edit config.json post-flash for non-Wi-Fi hookups.
  const [picoWifiSsid, setPicoWifiSsid] = useState('');
  const [picoWifiPsk, setPicoWifiPsk] = useState('');
  // lite-bundle-decouple slug: which helper preset the bundle ships
  // with. 'none' = echo only (default for a generalized lite node);
  // 'microcontroller' = sensor + wifi-status helpers + a wifi block in
  // config.json (the Pico starter kit). Picker only shown when the
  // operator picks the lite substrate (was: Pico Node).
  const [litePreset, setLitePreset] = useState('none');
  // lite-secure-profile slug: which security profile lite_node.py runs.
  // 'lite' = HMAC handshake + plaintext envelope (default, Pico-safe).
  // 'full' = X25519 ECDH handshake + AEAD envelope (generalized secure
  // node on a CPython host). microcontroller preset forces lite — the
  // Pico can't do ECDH/AEAD in pure MicroPython.
  const [liteProfile, setLiteProfile] = useState('lite');
  // admin-display-add-node: mark a lite node as a DISPLAY (screen) at provision
  // time — the operator picks an account and the BFF mints its frame-fetch read
  // key and writes it into the new station config (the display-cred-via-config
  // path the board polls), so the screen renders with no Edit-Station round-trip.
  // Optional (default off — most lite nodes aren't screens); a typeahead over the
  // same /api/user-access/accounts search DisplayCredentialSection uses.
  const [displayIsScreen, setDisplayIsScreen] = useState(false);
  const [displayQuery, setDisplayQuery] = useState('');
  const [displayResults, setDisplayResults] = useState([]);
  const [displayAccount, setDisplayAccount] = useState(null);
  // jvm-free-python-worker: function-set names a lite/worker node may CALL
  // across the mesh (referenced helpers). Baked into the bundle so a helper
  // authored on the node can `await referenced.<Set>.<fn>()`. Catalog comes
  // from the parent's network; an empty selection ships no referenced helpers.
  const [availableReferenced, setAvailableReferenced] = useState([]);
  const [referencedLoading, setReferencedLoading] = useState(false);
  const [selectedReferenced, setSelectedReferenced] = useState([]);
  // network-helpers-admin-ui: networks this node imports (canonical names).
  const [availableNetworks, setAvailableNetworks] = useState([]);
  const [importedLoading, setImportedLoading] = useState(false);
  const [selectedImported, setSelectedImported] = useState([]);
  const [internalHost, setInternalHost] = useState('');
  // Per-network helper inventory fetched from NodeService.get_helpers
  // (root-node-type slug). Hook returns { helpers, loading, error };
  // `helpers` is an array of { module, display_name, kind, … }.
  const { helpers: networkHelpers, loading: helpersLoading, error: helpersError } =
    useNetworkHelpers(networkId);
  const availableHelpers = networkHelpers.map((h) => h.module);
  // Helpers picker. Default once the inventory loads: all available
  // helpers selected = publish all = send no helpers_* node_prop.
  // Operator unchecks what they don't want; submit() picks the smaller
  // encoding (included if minority checked, excluded if majority).
  // All-unchecked = bare-relay sentinel ('__none__'); clearing every
  // box is the bare-relay UX (supersedes v0.58.2's dedicated checkbox).
  const [selectedHelpers, setSelectedHelpers] = useState(() => new Set());
  const [helpersInitialized, setHelpersInitialized] = useState(false);
  useEffect(() => {
    // Default-select all once the inventory arrives. Guards against
    // re-defaulting after the operator has already touched the grid:
    // once initialized, async refreshes don't reset the selection.
    if (!helpersInitialized && availableHelpers.length > 0) {
      setSelectedHelpers(new Set(availableHelpers));
      setHelpersInitialized(true);
    }
  }, [availableHelpers, helpersInitialized]);
  // Helper-config wizard state. `envValues[id]` holds whatever the
  // operator typed for HELPER_ENV_CONFIGS[id]; `envErrors[id]` is
  // either null or the validator's complaint. `step` is 0 for the
  // main form, then 1..N for each active helper-config screen.
  // Generalizes the v0.58.2 sensorPicos one-off field via the
  // HelperProcessor plugin registry on the BFF side.
  const [envValues, setEnvValues] = useState({});
  const [envErrors, setEnvErrors] = useState({});
  // admin-sectioned-dialogs: free navigation replaces the linear wizard `step`.
  const [activeCat, setActiveCat] = useState('basics');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // For standalone substrates: track the installer build task. The
  // InstallerBuildPanel below owns the polling; we just hold the id.
  const [installerTaskId, setInstallerTaskId] = useState(null);
  // Build status reported up by InstallerBuildPanel (standalone adds
  // only). The "Done" button stays disabled while the .run is still
  // building so the operator doesn't dismiss before the download link
  // exists. Non-standalone adds never mount the panel → no gate.
  const [buildStatus, setBuildStatus] = useState('pending');

  // Toggle one helper's checked-state. memberSet stays the canonical
  // source; submit() reads it to compute the on-wire helpers_* shape.
  const toggleHelper = (h) => setSelectedHelpers((prev) => {
    const next = new Set(prev);
    if (next.has(h)) next.delete(h); else next.add(h);
    return next;
  });
  // Derived: are we in bare-relay state? (Zero helpers selected.)
  const isBareRelay = selectedHelpers.size === 0;
  const isStandalone = substrate.startsWith('standalone-');
  const isPico = substrate === 'pico';
  // Which substrates can actually be referenced-helper CONSUMERS: standalone
  // (qlink/CPython) always, and lite only on the `worker` preset (the JVM-free
  // CPython consumer flavor). A `none`/`microcontroller` Pico can't run the
  // consumer, so it must neither show the picker nor ship referenced_helpers.
  const showReferenced = isStandalone || (isPico && litePreset === 'worker');

  // jvm-free-python-worker + add-node-referenced-parity: load the network's
  // referenceable function sets for a consumer-capable substrate. Mirrors
  // BuildInstallerModal's picker; same hub-backed catalog.
  useEffect(() => {
    if (!showReferenced || networkId == null) {
      setAvailableReferenced([]);
      // Drop any selection so a stale pick can't ship after switching to an
      // in-stack / non-worker substrate (it's only read on the consumer paths).
      setSelectedReferenced([]);
      return undefined;
    }
    let cancelled = false;
    setReferencedLoading(true);
    fetch(`/api/networks/${encodeURIComponent(String(networkId))}/referenced-functions`)
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (cancelled) return;
        const list = body && Array.isArray(body.referenced_functions)
          ? body.referenced_functions : [];
        setAvailableReferenced(list);
      })
      .catch(() => { if (!cancelled) setAvailableReferenced([]); })
      .finally(() => { if (!cancelled) setReferencedLoading(false); });
    return () => { cancelled = true; };
  }, [showReferenced, networkId]);

  const toggleReferenced = (refName) => {
    setSelectedReferenced((prev) => {
      const set = new Set(prev || []);
      if (set.has(refName)) set.delete(refName); else set.add(refName);
      return Array.from(set);
    });
  };

  // admin-display-add-node: debounced account typeahead for the display-screen
  // picker (only when the toggle is on and this is a lite node). Same
  // /api/user-access/accounts search DisplayCredentialSection uses, min 2 chars.
  useEffect(() => {
    if (!(isPico && displayIsScreen)) { setDisplayResults([]); return undefined; }
    const q = displayQuery.trim();
    if (q.length < 2) { setDisplayResults([]); return undefined; }
    let cancelled = false;
    const t = setTimeout(() => {
      fetch(`/api/user-access/accounts?q=${encodeURIComponent(q)}`,
            { credentials: 'same-origin' })
        .then((r) => (r.ok ? r.json() : null))
        .then((body) => {
          if (cancelled) return;
          setDisplayResults(((body && body.accounts) || []).slice(0, 20));
        })
        .catch(() => { if (!cancelled) setDisplayResults([]); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [isPico, displayIsScreen, displayQuery]);

  // network-helpers-admin-ui: load the importable networks (canonical names) for
  // a consumer-capable substrate; same gating as the referenced picker.
  useEffect(() => {
    if (!showReferenced) {
      setAvailableNetworks([]);
      setSelectedImported([]);
      return undefined;
    }
    let cancelled = false;
    setImportedLoading(true);
    fetch('/api/networks/')
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (cancelled) return;
        const list = body && Array.isArray(body.entity) ? body.entity
          : (Array.isArray(body) ? body : []);
        setAvailableNetworks(list);
      })
      .catch(() => { if (!cancelled) setAvailableNetworks([]); })
      .finally(() => { if (!cancelled) setImportedLoading(false); });
    return () => { cancelled = true; };
  }, [showReferenced]);

  const toggleImported = (canonical) => {
    setSelectedImported((prev) => {
      const set = new Set(prev || []);
      if (set.has(canonical)) set.delete(canonical); else set.add(canonical);
      return Array.from(set);
    });
  };
  // Derived: which helper-env config screens are active for the
  // current selection. Only meaningful for standalone substrates —
  // in-stack nodes don't go through build-installer.sh stamping.
  const activeEnvConfigs = isStandalone
    ? HELPER_ENV_CONFIGS.filter((c) =>
        c.consumedBy.some((h) => selectedHelpers.has(h)))
    : [];
  // admin-sectioned-dialogs: with free category navigation there's no linear
  // step to validate — validate everything at submit. Required fields (name,
  // in-stack docker name) already gate the primary button via each category's
  // `complete` flag; this re-checks them defensively and runs the helper-config
  // format validators, jumping to the offending category on failure.
  const validateAll = () => {
    if (!name.trim()) { setActiveCat('basics'); setError('Name is required.'); return false; }
    if (substrate === 'in-stack' && !internalHost.trim()) {
      setActiveCat('instack');
      setError('In-stack substrate requires a docker service name.');
      return false;
    }
    for (const cfg of activeEnvConfigs) {
      const err = cfg.validate(envValues[cfg.id]);
      if (err) {
        setEnvErrors((prev) => ({ ...prev, [cfg.id]: err }));
        setActiveCat(`cfg_${cfg.id}`);
        setError(null);
        return false;
      }
    }
    setError(null);
    return true;
  };

  const submit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    // validateAll covers Name + internal-host + the helper-config format
    // validators, navigating to the offending category on failure.
    if (!validateAll()) return;
    // admin-display-add-node: the display toggle without a chosen account is
    // almost certainly a mistake — block rather than silently skip provisioning.
    if (isPico && displayIsScreen && !displayAccount) {
      setError('Pick a display account, or uncheck "This node is a display".');
      return;
    }
    setSubmitting(true);
    setError(null);
    setResult(null);
    setInstallerTaskId(null);

    try {
      // parent_node_id is always set from the prop — there's no orphan
      // option after the Phase 4 child-of refactor. The new node
      // inherits parent's network_id server-side (provision_node SP).
      const baseBody = { name: name.trim(), parent_node_id: parentNodeId };

      let path;
      let body;
      if (isPico) {
        // lite-node slug: Pico Node provisioning returns a zip stream
        // instead of JSON. Handle the fetch + download here and exit;
        // the standalone/in-stack JSON path below stays untouched.
        // lite-secure-profile: microcontroller preset forces 'lite'
        // (Pico hardware constraint). UI mirrors the BFF guard so the
        // submitted POST body is consistent — operators who switch the
        // preset after picking 'full' don't ship a stale full flag.
        // kiosk-browser-display: kiosk hosts are Pi-class CPython — force
        // 'full' so crypto.py + the AEAD envelope path always ship.
        const effectiveProfile = litePreset === 'microcontroller' ? 'lite'
          : litePreset === 'kiosk' ? 'full' : liteProfile;
        const picoBody = {
          name: name.trim(),
          parent_node_id: parentNodeId,
          helpers_preset: litePreset,
          profile: effectiveProfile,
          // lite-secure-profile (absorbing lite-installer-flow): request
          // a self-extracting .run by default (matches standalone
          // substrates' UX). Operator scripts that still want a zip can
          // POST directly with `output_format: "zip"`.
          output_format: 'run',
          wifi_ssid: picoWifiSsid.trim(),
          wifi_psk: picoWifiPsk,
        };
        // jvm-free-python-worker: function-set names this node may CALL across
        // the mesh. Omit when none picked (the BFF treats absent as "no refs").
        if (selectedReferenced.length > 0) {
          picoBody.referenced_helpers = selectedReferenced;
        }
        // network-helpers-admin-ui: canonical networks this node imports.
        if (selectedImported.length > 0) {
          picoBody.imported_networks = selectedImported;
        }
        // admin-display-add-node: designate this lite node as a display and
        // provision its frame-fetch read key to the picked account at mint time.
        if (displayIsScreen && displayAccount) {
          picoBody.display_account_id = displayAccount.id;
        }
        const r = await fetch('/api/nodes/pico', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(picoBody),
        });
        if (!r.ok) {
          const txt = await r.text();
          let parsed = null;
          try { parsed = JSON.parse(txt); } catch (_) {}
          throw new Error((parsed && (parsed.message || parsed.error)) || txt || `HTTP ${r.status}`);
        }
        const blob = await r.blob();
        const nodeId = r.headers.get('X-Aveq-Node-Id') || '';
        // lite-bundle-decouple: filename matches the preset shape so the
        // operator's download trail isn't surprising (`pico-node-<id>` for
        // the Pico starter kit, `lite-node-<id>` for a generalized lite node).
        // lite-secure-profile: extension follows X-Aveq-Output-Format
        // (defaults to .run, the new installer-shaped UX).
        const fnamePrefix = litePreset === 'microcontroller' ? 'pico-node'
          : litePreset === 'kiosk' ? 'kiosk-node' : 'lite-node';
        const outFmt = r.headers.get('X-Aveq-Output-Format') || 'run';
        const ext = outFmt === 'zip' ? 'zip' : 'run';
        const fname = `${fnamePrefix}-${nodeId || 'new'}.${ext}`;
        // self-review #6: warn if referenced helpers were selected but the
        // server couldn't bake them (hub blip / unresolved / render missing) —
        // otherwise the worker ships referenced-free with no signal.
        const referencedNote = (selectedReferenced.length > 0
          && r.headers.get('X-Aveq-Referenced-Baked') !== 'true')
          ? ' ⚠ Referenced helpers were selected but could NOT be baked into the'
            + ' bundle (hub unreachable or none resolved) — the worker ships'
            + ' without them; rebuild to retry.'
          : '';
        // admin-display-add-node: surface the display-credential outcome. The
        // header is '' when no display account was picked; 'provisioned' | 'failed'
        // otherwise. A failure is non-fatal (the node still provisioned) — the
        // operator can retry from Edit Station → Display credential.
        const displayCred = r.headers.get('X-Aveq-Display-Credential') || '';
        const displayNote = displayCred === 'provisioned'
          ? ' Display credential provisioned for the picked account — the screen'
            + ' picks it up on its first config refresh, no re-flash.'
          : displayCred === 'failed'
            ? ' ⚠ The node provisioned, but its display credential could NOT be'
              + ' minted/written (see server logs) — provision it from Edit'
              + ' Station → Display credential.'
            : '';
        // Browser-side blob download. Same idiom the standalone path uses
        // for installer .run downloads — see InstallerBuildPanel.
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fname;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setResult({
          message: (ext === 'run'
            ? (litePreset === 'microcontroller'
                ? `Provisioned Pico node ${nodeId}. Installer downloaded as ${fname}; run it (\`./${fname}\`) to extract + deploy via mpremote.`
                : `Provisioned lite node ${nodeId}. Installer downloaded as ${fname}; run it (\`./${fname}\`) on a CPython host to extract + set up the venv.`)
            : (litePreset === 'microcontroller'
                ? `Provisioned Pico node ${nodeId}. Bundle downloaded as ${fname}; mpremote cp the files onto the device then reset.`
                : `Provisioned lite node ${nodeId}. Bundle downloaded as ${fname}; deploy on any CPython host (or microcontroller via mpremote).`))
            + referencedNote + displayNote,
          nodeId,
        });
        setSubmitting(false);
        return;
      }
      if (isStandalone) {
        path = '/api/nodes/standalone';
        body = {
          ...baseBody,
          flavor: substrate === 'standalone-docker' ? 'docker' : 'bare-metal',
        };
        // dev-bundle-autobuild slug: per-arch docker recipient — send the chosen
        // arch so the backend builds/uses the matching bundle. Without this the
        // backend defaults amd64 (build-installer.sh), which is the bug that made
        // arm64 docker adds fail. bare-metal is arch-neutral, so omit it there.
        if (substrate === 'standalone-docker') {
          body.bundle_arch = bundleArch;
        }
        // add-node-referenced-parity: function-set names this node may CALL
        // across the mesh. Stamped + baked at provision time (the backend
        // forwards --referenced to build-installer). Omit when none picked.
        if (selectedReferenced.length > 0) {
          body.referenced_helpers = selectedReferenced;
        }
        // network-helpers-admin-ui: canonical networks this node imports.
        if (selectedImported.length > 0) {
          body.imported_networks = selectedImported;
        }
        // Helper-config map. Each active wizard step produces one
        // entry per consuming helper that's selected, keyed by helper
        // module name. The backend's HelperProcessor registry decides
        // what each one does (env stamp, node_prop, etc.). Generalizes
        // the v0.58.2 sensor_picos one-off field.
        const helperConfigs = {};
        for (const cfg of activeEnvConfigs) {
          const raw = (envValues[cfg.id] || '').trim();
          if (!raw) continue;
          const payload = cfg.payloadFor(raw);
          for (const helper of cfg.consumedBy) {
            if (selectedHelpers.has(helper)) {
              helperConfigs[helper] = payload;
            }
          }
        }
        if (Object.keys(helperConfigs).length > 0) {
          body.helper_configs = helperConfigs;
        }
        // Helpers — pick the smaller list (included vs excluded)
        // automatically. Three cases:
        //   - 0 checked     → bare relay sentinel (__none__)
        //   - all checked   → no prop sent (publish-all default)
        //   - partial       → smaller of included/excluded wins
        const chosen = availableHelpers.filter((h) => selectedHelpers.has(h));
        const unchosen = availableHelpers.filter((h) => !selectedHelpers.has(h));
        if (chosen.length === 0) {
          body.helpers_included = '__none__';
        } else if (unchosen.length === 0) {
          // All checked = publish-all default; send nothing.
        } else if (chosen.length <= unchosen.length) {
          body.helpers_included = chosen.join(',');
        } else {
          body.helpers_excluded = unchosen.join(',');
        }
      } else {
        path = '/api/nodes/';
        body = { ...baseBody, internal_host: internalHost.trim() };
      }

      const r = await fetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const text = await r.text();
      let parsed = null;
      try { parsed = JSON.parse(text); } catch (_) {}
      if (!r.ok) {
        throw new Error((parsed && (parsed.message || parsed.error)) || text || `HTTP ${r.status}`);
      }
      if (isStandalone) {
        // {node, installer_task_id}
        setResult(parsed.node);
        setInstallerTaskId(parsed.installer_task_id);
      } else {
        // {entity: {...}} or the credentials dict directly
        setResult(parsed && parsed.entity ? parsed.entity : parsed);
      }
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  const renderBasics = () => (
    <>
            <label style={labelStyle}>
              <span>Name</span>
              <input value={name} onChange={(e) => setName(e.target.value)}
                     autoFocus maxLength={64} required
                     placeholder="e.g. customer-acme-001" style={inputStyle} />
            </label>

            <fieldset style={fieldsetStyle}>
              <legend style={{ fontSize: '0.85rem', color: '#444', padding: '0 6px' }}>Substrate</legend>
              <label style={radioRowStyle}>
                <input type="radio" name="substrate" value="in-stack"
                       checked={substrate === 'in-stack'}
                       onChange={(e) => setSubstrate(e.target.value)} />
                <span><strong>In-stack</strong> — docker service on this stack (parent dials via docker DNS)</span>
              </label>
              <label style={radioRowStyle}>
                <input type="radio" name="substrate" value="standalone-docker"
                       checked={substrate === 'standalone-docker'}
                       onChange={(e) => setSubstrate(e.target.value)} />
                <span><strong>Standalone — Docker</strong> — off-stack recipient with Docker runtime</span>
              </label>
              {substrate === 'standalone-docker' && (
                <div style={{ margin: '0 0 4px 26px', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.8rem', color: '#555' }}>Target arch:</span>
                  <label style={{ fontSize: '0.8rem', display: 'flex', gap: 4, alignItems: 'center' }}>
                    <input type="radio" name="bundle_arch" value="amd64"
                           checked={bundleArch === 'amd64'}
                           onChange={(e) => setBundleArch(e.target.value)} />
                    <span>amd64 <span style={{ color: '#888' }}>— x86 servers</span></span>
                  </label>
                  <label style={{ fontSize: '0.8rem', display: 'flex', gap: 4, alignItems: 'center' }}>
                    <input type="radio" name="bundle_arch" value="arm64"
                           checked={bundleArch === 'arm64'}
                           onChange={(e) => setBundleArch(e.target.value)} />
                    <span>arm64 <span style={{ color: '#888' }}>— Apple Silicon, Pi, ARM</span></span>
                  </label>
                </div>
              )}
              <label style={radioRowStyle}>
                <input type="radio" name="substrate" value="standalone-bare-metal"
                       checked={substrate === 'standalone-bare-metal'}
                       onChange={(e) => setSubstrate(e.target.value)} />
                <span><strong>Standalone — Bare-metal</strong> — off-stack recipient with Java 21 + Python 3 (no Docker)</span>
              </label>
              <label style={radioRowStyle}>
                <input type="radio" name="substrate" value="pico"
                       checked={substrate === 'pico'}
                       onChange={(e) => setSubstrate(e.target.value)} />
                <span><strong>Lite Node</strong> — minimal Python runtime (Pico, capable host, …)</span>
              </label>
            </fieldset>
    </>
  );

  const renderInstack = () => (
              <label style={labelStyle}>
                <span>Docker service name</span>
                <input value={internalHost} onChange={(e) => setInternalHost(e.target.value)}
                       placeholder="e.g. qlink3" style={inputStyle} required />
                <small style={{ color: '#888', fontSize: '0.75rem' }}>
                  Parent dials this node directly via <code>ws://{'<name>'}:4567/s/link</code> instead of routing through the rendezvous.
                </small>
              </label>
  );

  const renderLite = () => (
              <>
                {/* lite-bundle-decouple slug: helper-bundle preset.
                    "Pico starter kit" re-adds today's exact behavior
                    (sensor + wifi-status helpers + wifi config block);
                    "None" ships a generalized lite node with just the
                    echo helper and no wifi expectation. */}
                <label style={labelStyle}>
                  <span>Helper bundle</span>
                  <select value={litePreset} onChange={(e) => setLitePreset(e.target.value)}
                          style={inputStyle}>
                    <option value="none">None — echo helper only (default)</option>
                    <option value="microcontroller">Pico starter kit — sensor + wifi-status helpers</option>
                    <option value="worker">JVM-free worker — CPython consumer (referenced helpers)</option>
                    <option value="weather">Weather — forecast + comfort helper (CPython lite)</option>
                    <option value="plant-care">Plant care — watering verdict from WeatherForecast (CPython lite)</option>
                    <option value="kiosk">Kiosk display — browser second screen (Pi + Chromium)</option>
                  </select>
                  <small style={{ color: '#888', fontSize: '0.75rem' }}>
                    {litePreset === 'microcontroller'
                      ? 'Bundle ships sensor + wifi-status helpers; config.json gets a wifi block populated below.'
                      : litePreset === 'worker'
                        ? 'JVM-free CPython worker: same lite runtime, no qbric jar. Ships an example consumer helper; pick referenced helpers below for it to call across the mesh.'
                        : litePreset === 'kiosk'
                          ? 'Browser-kiosk display node: supervised Chromium showing the live station output. Bundle ships the kiosk runtime + install.sh (venv + systemd unit); the device needs Chromium and a graphical session.'
                          : 'Bundle ships only the runtime + echo demo helper; recipient has wired/managed connectivity.'}
                  </small>
                </label>
                {/* lite-secure-profile slug: security profile picker.
                    `lite` = HMAC handshake + plaintext envelope (Pico
                    default). `full` = X25519 ECDH handshake + AEAD
                    envelope (generalized secure node on a CPython host;
                    aveq-admin mints an X25519 keypair, stamps the
                    privkey into config.json, lite_node self-publishes
                    the pubkey on boot). Disabled + forced to `lite` when
                    the microcontroller preset is on — Pico hardware
                    can't run the full crypto path. */}
                <label style={labelStyle}>
                  <span>Security profile</span>
                  <select value={litePreset === 'microcontroller' ? 'lite' : liteProfile}
                          onChange={(e) => setLiteProfile(e.target.value)}
                          disabled={litePreset === 'microcontroller'}
                          style={inputStyle}>
                    <option value="lite">Lite — HMAC handshake + plaintext envelope</option>
                    <option value="full">Full — X25519 ECDH handshake + AEAD envelope</option>
                  </select>
                  <small style={{ color: '#888', fontSize: '0.75rem' }}>
                    {litePreset === 'microcontroller'
                      ? 'Forced to lite — Pico hardware (RP2350 / MicroPython) can\'t run ECDH/AEAD in the runtime budget.'
                      : (liteProfile === 'full'
                          ? 'aveq-admin mints an X25519 keypair; the private key rides in config.json, the public key is auto-published on first boot.'
                          : 'Plaintext envelope with CRC32 integrity. Same as today\'s Pico path.')}
                  </small>
                </label>
                {litePreset === 'microcontroller' && (
                  <>
                    <label style={labelStyle}>
                      <span>Wi-Fi SSID (optional)</span>
                      <input value={picoWifiSsid} onChange={(e) => setPicoWifiSsid(e.target.value)}
                             placeholder="my-network" style={inputStyle} />
                    </label>
                    <label style={labelStyle}>
                      <span>Wi-Fi password (optional)</span>
                      <input value={picoWifiPsk} onChange={(e) => setPicoWifiPsk(e.target.value)}
                             placeholder="" type="password" style={inputStyle} />
                      <small style={{ color: '#888', fontSize: '0.75rem' }}>
                        Stamped into the bundle's <code>config.json</code>. Leave blank to edit on the device after flashing (e.g. for Ethernet or OTA-provisioned creds).
                      </small>
                    </label>
                  </>
                )}
                {/* admin-display-add-node: designate this lite node as a DISPLAY
                    and provision its frame-fetch read key to a chosen account at
                    mint time. Optional — most lite nodes aren't screens. The key
                    is written into the new station config (display-cred-via-config),
                    NOT baked into the bundle; the board polls it on first boot. */}
                <label style={{ ...labelStyle, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={displayIsScreen}
                         onChange={(e) => { setDisplayIsScreen(e.target.checked);
                                            if (!e.target.checked) { setDisplayAccount(null); setDisplayQuery(''); } }} />
                  <span style={{ margin: 0 }}>This node is a display (screen)</span>
                </label>
                {displayIsScreen && (
                  <div style={{ margin: '2px 0 6px 26px' }}>
                    <small style={{ color: '#888', fontSize: '0.75rem', display: 'block', marginBottom: 4 }}>
                      Provision the read key the screen uses to fetch rendered frames. aveq-admin mints it
                      and writes it into the station config — the board picks it up with no re-flash. The
                      key is a device secret and is never shown. Tip: use a dedicated low-access “display”
                      account (ADR D4).
                    </small>
                    {displayAccount ? (
                      <div style={{ fontSize: '0.82rem' }}>
                        Grant to <strong>{displayAccount.display_name || `account ${displayAccount.id}`}</strong>
                        {displayAccount.email && <span style={{ color: '#888' }}> · {displayAccount.email}</span>}
                        <button type="button"
                                onClick={() => { setDisplayAccount(null); setDisplayQuery(''); }}
                                style={{ marginLeft: 8, fontSize: '0.75rem', cursor: 'pointer' }}>change</button>
                      </div>
                    ) : (
                      <>
                        <input type="text" value={displayQuery}
                               onChange={(e) => setDisplayQuery(e.target.value)}
                               placeholder="Search account by name or email (min 2 chars)…"
                               style={inputStyle} />
                        {displayResults.length > 0 && (
                          <div style={{ border: '1px solid #e2e5ea', borderRadius: 4, marginTop: 4,
                                        maxHeight: 140, overflowY: 'auto' }}>
                            {displayResults.map((a) => (
                              <div key={a.id} onClick={() => { setDisplayAccount(a); setDisplayResults([]); }}
                                   style={{ padding: '5px 8px', cursor: 'pointer', fontSize: '0.82rem',
                                            borderBottom: '1px solid #f0f1f3' }}>
                                <strong>{a.display_name || `account ${a.id}`}</strong>
                                {a.email && <span style={{ color: '#888' }}> · {a.email}</span>}
                                <span style={{ color: '#bbb' }}> · #{a.id}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </>
  );

  const renderHelpers = () => (
              <label style={labelStyle}>
                <span>
                  Helpers
                  <small style={{ color: '#888', fontWeight: 400, marginLeft: 6 }}>
                    {(() => {
                      if (helpersLoading) return '— loading inventory…';
                      if (helpersError) return '— inventory unavailable';
                      const c = selectedHelpers.size;
                      const t = availableHelpers.length;
                      if (t === 0) return '— root inventory is empty';
                      if (c === 0) return '— bare relay (no built-in helpers)';
                      if (c === t) return `— publish all (${t})`;
                      const u = t - c;
                      return c <= u
                        ? `— include only ${c} of ${t}`
                        : `— exclude ${u} of ${t}`;
                    })()}
                  </small>
                </span>
                {helpersError && (
                  <div style={{ color: '#a00', fontSize: '0.8rem', padding: '6px 0' }}>
                    Couldn't load helpers from network root: {helpersError}
                  </div>
                )}
                {!helpersLoading && !helpersError && availableHelpers.length === 0 && (
                  <div style={{ color: '#888', fontSize: '0.8rem', padding: '6px 0' }}>
                    No helpers in this network's inventory. The recipient will
                    install as a bare relay; operator-mounted helpers under
                    /opt/ext/extra/ still load.
                  </div>
                )}
                {!helpersLoading && availableHelpers.length > 0 && (
                  <>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: '0.75rem' }}>
                      <button type="button"
                              onClick={() => setSelectedHelpers(new Set(availableHelpers))}
                              style={miniLinkStyle}>select all</button>
                      <button type="button"
                              onClick={() => setSelectedHelpers(new Set())}
                              style={miniLinkStyle}>none (bare relay)</button>
                    </div>
                    <div style={helperGridStyle}>
                      {networkHelpers.map((h) => (
                        <label key={h.module} style={helperItemStyle}
                               title={h.description || ''}>
                          <input type="checkbox"
                                 checked={selectedHelpers.has(h.module)}
                                 onChange={() => toggleHelper(h.module)} />
                          <span>{h.display_name || h.module.replace(/_endpoints$/, '')}</span>
                        </label>
                      ))}
                    </div>
                  </>
                )}
                <small style={{ color: '#888', fontSize: '0.75rem' }}>
                  {isBareRelay
                    ? "Bare relay — only operator-mounted helpers under /opt/ext/extra/ load."
                    : "Operator-mounted helpers under /opt/ext/extra/ also load (not subject to this filter)."}
                </small>
              </label>
  );

  // add-node-referenced-parity: referenced-helper picker, shared by lite +
  // standalone substrates (both CALL helpers hosted on OTHER mesh nodes). The
  // selection bakes into the bundle so a helper authored on the node can call
  // `referenced.<Set>.<fn>()`. Distinct from the Helpers picker (helpers HOSTED).
  const renderReferenced = () => (
              <fieldset style={fieldsetStyle}>
                <legend style={{ fontSize: '0.85rem', color: '#444', padding: '0 6px' }}>
                  Referenced helpers (optional)
                </legend>
                {referencedLoading ? (
                  <div style={{ fontSize: '0.8rem', color: '#888', padding: '4px 2px' }}>
                    Loading referenceable functions…
                  </div>
                ) : (availableReferenced || []).length === 0 ? (
                  <div style={{ fontSize: '0.8rem', color: '#888', padding: '4px 2px' }}>
                    Nothing referenceable from this network (no global or in-network
                    helpers exposed). Leave blank.
                  </div>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 6, fontSize: '0.75rem' }}>
                      <span style={{ color: '#999', marginLeft: 'auto' }}>
                        {(selectedReferenced || []).length} / {(availableReferenced || []).length} selected
                      </span>
                    </div>
                    <div style={{ padding: '2px 4px' }}>
                      {(availableReferenced || []).map((fs) => (
                        <label key={fs.name} style={{
                          display: 'flex', alignItems: 'flex-start', gap: 6,
                          fontSize: '0.8rem', marginBottom: 3, cursor: 'pointer',
                        }}>
                          <input type="checkbox"
                                 checked={(selectedReferenced || []).includes(fs.name)}
                                 onChange={() => toggleReferenced(fs.name)}
                                 style={{ marginTop: 3 }} />
                          <div>
                            <code>{fs.name}</code>
                            <span style={{ color: '#999', fontSize: '0.7rem' }}> · {fs.source}</span>
                            {fs.description && (
                              <span style={{ color: '#666' }}> — {fs.description}</span>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                  </>
                )}
                <small style={{ color: '#888', fontSize: '0.72rem', display: 'block', marginTop: 6 }}>
                  These run on OTHER nodes; their specs are baked into the bundle so
                  a helper authored here can call them via <code>referenced.&lt;Set&gt;.&lt;fn&gt;()</code>.
                </small>
              </fieldset>
  );

  // network-helpers-admin-ui: pick which networks this node IMPORTS. Importing
  // network X bakes X's public helpers (`X/<ns>:invoke`) into the bundle.
  const renderImported = () => (
              <fieldset style={fieldsetStyle}>
                <legend style={{ fontSize: '0.85rem', color: '#444', padding: '0 6px' }}>
                  Imported networks (optional)
                </legend>
                {importedLoading ? (
                  <div style={{ fontSize: '0.8rem', color: '#888', padding: '4px 2px' }}>
                    Loading networks…
                  </div>
                ) : (availableNetworks || []).length === 0 ? (
                  <div style={{ fontSize: '0.8rem', color: '#888', padding: '4px 2px' }}>
                    No networks to import. Leave blank.
                  </div>
                ) : (
                  <div style={{ padding: '2px 4px' }}>
                    {(availableNetworks || []).map((nw) => (
                      <label key={nw.canonical_name} style={{
                        display: 'flex', alignItems: 'center', gap: 6,
                        fontSize: '0.8rem', marginBottom: 3, cursor: 'pointer',
                      }}>
                        <input type="checkbox"
                               checked={(selectedImported || []).includes(nw.canonical_name)}
                               onChange={() => toggleImported(nw.canonical_name)} />
                        <div>
                          <code>{nw.canonical_name}</code>
                          {nw.display_name && (
                            <span style={{ color: '#999', fontSize: '0.7rem' }}> · {nw.display_name}</span>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
                <small style={{ color: '#888', fontSize: '0.72rem', display: 'block', marginTop: 6 }}>
                  Bakes each imported network's public helpers as
                  <code> &lt;network&gt;/&lt;ns&gt;:invoke</code>; the §6 gate lets this node call them.
                </small>
              </fieldset>
  );

  // Helper-config detail pane (per active HELPER_ENV_CONFIGS entry). The
  // category stays visible but LOCKED until one of its consuming helpers is
  // selected in Helpers — the req-3 dependency-lock showcase.
  const renderHelperConfig = (cfg) => {
    const err = envErrors[cfg.id];
    return (
      <>
        <label style={labelStyle}>
          <span>{cfg.title}</span>
          <textarea
            value={envValues[cfg.id] || ''}
            onChange={(e) => {
              setEnvValues((prev) => ({ ...prev, [cfg.id]: e.target.value }));
              if (envErrors[cfg.id]) {
                setEnvErrors((prev) => ({ ...prev, [cfg.id]: null }));
              }
            }}
            placeholder={cfg.placeholder}
            rows={3}
            style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '0.85rem' }} />
          {err && <small style={{ color: '#c00', fontSize: '0.75rem' }}>{err}</small>}
          <small style={{ color: '#888', fontSize: '0.75rem' }}>
            Stamped into the recipient's <code>.env</code> at build time. NOT stored as
            node config; the recipient can edit <code>/etc/aveq/qlink/.env</code> and
            restart <code>qlink</code> later. Leave blank to skip — the helper handles
            the empty case.
          </small>
        </label>
      </>
    );
  };

  // admin-sectioned-dialogs: substrate (Basics) drives which categories apply
  // (grow/shrink). A helper-config category appears ONLY once a consuming helper
  // is selected in Helpers, and is hidden otherwise — the dependency is handled
  // by grow/shrink rather than a visible-but-locked section.
  const categories = [
    { id: 'basics', label: 'Basics', description: 'Provisions a fresh qbric node under this parent (it inherits the parent network). Name and substrate are required.', hasRequired: true, complete: name.trim() !== '', render: renderBasics },
    { id: 'instack', label: 'Docker service', description: 'How the parent dials this in-stack node.', applicable: substrate === 'in-stack', hasRequired: true, complete: internalHost.trim() !== '', render: renderInstack },
    { id: 'lite', label: 'Lite node', description: 'Helper preset, security profile, and (Pico) Wi-Fi for the lite runtime.', applicable: isPico, render: renderLite },
    { id: 'helpers', label: 'Helpers', description: 'Helpers this node hosts. Uncheck all for a bare relay.', applicable: isStandalone, render: renderHelpers },
    { id: 'referenced', label: 'Referenced helpers', description: 'Helpers on OTHER nodes this node may call across the mesh.', applicable: showReferenced, render: renderReferenced },
    { id: 'imported', label: 'Imported networks', description: 'Networks whose public helpers this node may call.', applicable: showReferenced, render: renderImported },
    ...HELPER_ENV_CONFIGS.map((cfg) => ({
      id: `cfg_${cfg.id}`,
      label: cfg.title,
      description: cfg.description,
      // Hidden until a consuming helper is selected (grow/shrink).
      applicable: isStandalone && cfg.consumedBy.some((h) => selectedHelpers.has(h)),
      error: envErrors[cfg.id] || null,
      render: () => renderHelperConfig(cfg),
    })),
  ];

  const resultNode = result ? (
    <>
      <h2 style={{ marginTop: 0, color: '#1e8e3e' }}>Node provisioned</h2>
      {result.message ? (
        // Lite/Pico bundle path: the bundle already downloaded in-browser, so
        // there are no api_key/token rows to save — show the human-readable
        // outcome instead (download filename + any referenced-helper /
        // admin-display-add-node display-credential notes). Previously this
        // message was set but never rendered, so those notes were invisible.
        <p style={{ fontSize: '0.9rem', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
          {result.message}
        </p>
      ) : (
        <NodeCredentialsPanel
          credentials={result}
          warning="⚠ Save these credentials into the new node's api-config.yml. You can re-open this panel from the node detail view if needed." />
      )}
      {installerTaskId && (
        <InstallerBuildPanel taskId={installerTaskId} onStatusChange={setBuildStatus} />
      )}
    </>
  ) : null;

  const done = () => { onSuccess && onSuccess(); onClose(); };
  const resultFooter = (installerTaskId && buildStatus === 'pending') ? (
    <>
      <button type="button" onClick={done} style={buttonSecondaryStyle}>Close anyway</button>
      <button type="button" disabled style={buttonDisabledStyle}>Building installer…</button>
    </>
  ) : (
    <button type="button" onClick={done} style={buttonPrimaryStyle}>Done</button>
  );

  return (
    <SectionedDialog
      title={<>Add child of <code style={{ fontSize: '0.85em', color: '#555' }}>{parentNodeName || `node ${parentNodeId}`}</code></>}
      categories={categories}
      activeId={activeCat}
      onSelect={setActiveCat}
      primaryLabel={isStandalone ? 'Provision + build installer' : 'Add Node'}
      submittingLabel="Provisioning…"
      submitting={submitting}
      onPrimary={submit}
      onCancel={onClose}
      error={error}
      result={resultNode}
      resultFooter={resultFooter}
      width={760}
    />
  );
}

const labelStyle = {
  display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12,
  fontSize: '0.85rem',
};
const inputStyle = {
  padding: '6px 8px', fontSize: '0.9rem', border: '1px solid #ccc', borderRadius: 4,
};
const fieldsetStyle = {
  border: '1px solid #ddd', borderRadius: 4, padding: '8px 12px 4px',
  marginBottom: 12,
};
const radioRowStyle = {
  display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 6,
  fontSize: '0.85rem', cursor: 'pointer',
};
// 2-column responsive grid for the helper checkboxes. Compact rows so
// 15 entries fit without scrolling the modal.
const helperGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '2px 12px',
  border: '1px solid #ddd', borderRadius: 4, padding: '8px 12px',
  marginBottom: 4,
};
const helperItemStyle = {
  display: 'flex', alignItems: 'center', gap: 6,
  fontSize: '0.8rem', cursor: 'pointer', fontWeight: 400,
  padding: '2px 0',
};
const miniLinkStyle = {
  background: 'none', border: 'none', color: '#1a73e8',
  cursor: 'pointer', padding: '2px 4px', fontSize: '0.75rem',
};
const buttonPrimaryStyle = {
  padding: '8px 16px', background: '#5fa67a', color: '#fff', border: 'none',
  borderRadius: 4, fontSize: '0.85rem', cursor: 'pointer',
};
const buttonSecondaryStyle = {
  padding: '8px 16px', background: 'transparent', color: '#5fa67a',
  border: '1px solid #5fa67a', borderRadius: 4, fontSize: '0.85rem', cursor: 'pointer',
};
const buttonDisabledStyle = {
  padding: '8px 16px', background: '#b9d4c4', color: '#fff', border: 'none',
  borderRadius: 4, fontSize: '0.85rem', cursor: 'not-allowed',
};
